import { FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import { useFormContext } from '../../../../../contexts/FormContext';
import { Campaign } from '../../../../../types/Campaign.type';
import {
  AudienceRetargetInput,
  BlogInput,
  CallToActionInput,
  EmailInput,
  FeatureDescriptionInput,
  FormType,
  HeroHeaderInput,
  SocialInput,
  SubheaderInput,
} from '../../../../../types/FormTypes';
import { NextImageBasicData } from '../../../../../types/Next';
import { BasicButtonSelectionInput } from '../../../../shared/inputs/BasicButtonSelectionInput';
import {
  defaultDemographics,
  socialDefaultPlatformIcons,
  suggestedDemographics,
} from './DefaultOptions';

type TargetDemographicInputsProps<T> = {
  formikProps: FormikProps<T>;
  disabled: boolean;
  title?: string;
  subtitle?: string;
};

export function TargetDemographicInputs<
  T extends
    | BlogInput
    | SocialInput
    | EmailInput
    | AudienceRetargetInput
    | Campaign
    | HeroHeaderInput
    | SubheaderInput
    | FeatureDescriptionInput
    | CallToActionInput
>({
  formikProps,
  disabled,
  title = 'Who is the targeted demographic?',
  subtitle = 'Describe your targeted demographic. Ex. Atlanta, parents, and marketers',
}: TargetDemographicInputsProps<T>) {
  const { formType } = useFormContext();

  const [defaultDemographicOptions, setDefaultDemographicOptions] =
    useState(defaultDemographics);
  const [defaultSocialIcon, setDefaultSocialIcon] = useState<
    NextImageBasicData[]
  >([]);
  const [prevTargetDemographic, setPrevTargetDemographic] = useState<string[]>(
    formikProps.values.targetDemographic
  );

  useEffect(() => {
    let suggestedDemographicOption: string | undefined;
    let suggestedSocialIcon: NextImageBasicData[] = [];
    const castedSocialInput = formikProps.values as SocialInput;

    if (castedSocialInput.platform) {
      suggestedDemographicOption =
        suggestedDemographics[FormType.SOCIAL][castedSocialInput.platform];
      suggestedSocialIcon = [
        socialDefaultPlatformIcons[castedSocialInput.platform],
      ];
    }

    if (suggestedDemographicOption) {
      setDefaultDemographicOptions([
        suggestedDemographicOption,
        ...defaultDemographics,
      ]);
    } else {
      setDefaultDemographicOptions([...defaultDemographics]);
    }

    setDefaultSocialIcon(suggestedSocialIcon);
  }, [formType, formikProps]);

  // If suggested demographic is selected, it can be the only demographic selected
  useEffect(() => {
    if (formikProps.values.targetDemographic.length > 1) {
      const prevSuggestedIndex = prevTargetDemographic.findIndex(
        (demographic) => demographic.includes('suggested')
      );
      const newSuggestedIndex = formikProps.values.targetDemographic.findIndex(
        (demographic) => demographic.includes('suggested')
      );
      // Suggested demographic has been newly selected
      if (newSuggestedIndex > -1 && prevSuggestedIndex === -1) {
        formikProps.setFieldValue('targetDemographic', [
          formikProps.values.targetDemographic[newSuggestedIndex],
        ]);
      } else if (prevSuggestedIndex > -1 && newSuggestedIndex > -1) {
        // Other demographics were selected in addition to the suggested tone
        const newTargetDemographic = [...formikProps.values.targetDemographic];
        newTargetDemographic.splice(newSuggestedIndex, 1);
        formikProps.setFieldValue('targetDemographic', newTargetDemographic);
      }
    }
    setPrevTargetDemographic(formikProps.values.targetDemographic);
  }, [formikProps.values.targetDemographic]);

  return (
    <BasicButtonSelectionInput
      id="targetDemographic"
      isMultipleSelection
      allowUserInput
      label={
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
      }
      defaultOptions={defaultDemographicOptions}
      defaultOptionIcons={defaultSocialIcon}
      value={formikProps.values.targetDemographic}
      disabled={disabled}
      errors={formikProps.errors.targetDemographic as string}
      touched={formikProps.touched.targetDemographic ? true : false}
      onBlur={formikProps.handleBlur}
    />
  );
}
